/**
 * Translation Utilities
 *
 * Helper functions for merging translated fields with base entities.
 * Translation values only override base values when they are non-empty.
 * If a translation field is null/empty, the base (main table) value is preserved.
 */

import type { Database } from './database.types';

type ProductTypeTranslation = Database['public']['Tables']['product_type_translations']['Row'];
type MerchantTranslation = Database['public']['Tables']['merchant_translations']['Row'];
type LocationTranslation = Database['public']['Tables']['location_translations']['Row'];
type CategoryTranslation = Database['public']['Tables']['category_translations']['Row'];
type PricingRuleTranslation = Database['public']['Tables']['pricing_rule_translations']['Row'];
type InventoryItemTranslation = Database['public']['Tables']['inventory_item_translations']['Row'];

/**
 * Merge product type translation fields into the base product
 * If translation exists, override only fields that have actual translated values.
 * If no translation exists (default language), return base product unchanged.
 */
export function mergeProductTranslation<T extends Record<string, any>>(
  product: T,
  translation: ProductTypeTranslation | null
): T {
  if (!translation) {
    return product;
  }

  return {
    ...product,
    product_name: translation.product_name || product.product_name,
    description: translation.description || product.description,
    variant_label: translation.variant_label || product.variant_label,
    variant_description_label: translation.variant_description_label || product.variant_description_label,
    features: translation.features || product.features,
    product_specific_info: translation.product_specific_info || product.product_specific_info,
  };
}

/**
 * Merge merchant translation fields into the base merchant
 */
export function mergeMerchantTranslation<T extends Record<string, any>>(
  merchant: T,
  translation: MerchantTranslation | null
): T {
  if (!translation) {
    return merchant;
  }

  return {
    ...merchant,
    business_name: translation.business_name || merchant.business_name,
    description: translation.description || merchant.description,
    additional_text_checkout: translation.additional_text_checkout || merchant.additional_text_checkout,
    cancellation_policy_description: translation.cancellation_policy_description || merchant.cancellation_policy_description,
    custom_rental_periods_text: translation.custom_rental_periods_text || merchant.custom_rental_periods_text,
  };
}

/**
 * Merge location translation fields into the base location
 */
export function mergeLocationTranslation<T extends Record<string, any>>(
  location: T,
  translation: LocationTranslation | null
): T {
  if (!translation) {
    return location;
  }

  return {
    ...location,
    location_name: translation.location_name || location.location_name,
    pickup_address: translation.pickup_address || location.pickup_address,
    additional_text_checkout: translation.additional_text_checkout || location.additional_text_checkout,
  };
}

/**
 * Merge translated labels and placeholders into custom field configs.
 * Matches by `key`; an empty translated value keeps the base value, as the
 * booking frontend does.
 */
export function mergeCustomFieldTranslations<T extends { key: string; label: string; placeholder?: string }>(
  baseFields: T[],
  translatedFields: Array<{ key: string; label?: string; placeholder?: string }> | null
): T[] {
  if (!translatedFields || translatedFields.length === 0) {
    return baseFields;
  }

  const translationMap = new Map(translatedFields.map(f => [f.key, f]));

  return baseFields.map(field => {
    const tr = translationMap.get(field.key);
    if (!tr) return field;
    return {
      ...field,
      ...(tr.label ? { label: tr.label } : {}),
      ...(tr.placeholder ? { placeholder: tr.placeholder } : {}),
    };
  });
}

/**
 * Merge category translation fields into the base category
 */
export function mergeCategoryTranslation<T extends Record<string, any>>(
  category: T,
  translation: CategoryTranslation | null
): T {
  if (!translation) {
    return category;
  }

  return {
    ...category,
    category_name: translation.category_name || category.category_name,
    description: translation.description || category.description,
  };
}

/**
 * Merge pricing rule translation fields into the base pricing rule
 */
export function mergePricingRuleTranslation<T extends Record<string, any>>(
  rule: T,
  translation: PricingRuleTranslation | null
): T {
  if (!translation) {
    return rule;
  }

  return {
    ...rule,
    duration_label: translation.duration_label || rule.duration_label,
  };
}

/**
 * Merge inventory item translation fields into the base inventory item
 */
export function mergeInventoryItemTranslation<T extends Record<string, any>>(
  item: T,
  translation: InventoryItemTranslation | null
): T {
  if (!translation) {
    return item;
  }

  return {
    ...item,
    inventory_specific_info: translation.inventory_specific_info || item.inventory_specific_info,
  };
}
